import { useContext, useCallback, useState, useEffect } from "react";

import { OrgRecord } from "@/shared/domain/orgs/OrgRecord";

import { Auth, AuthContext } from "./AuthContext";

export const useAuth = (): Auth => {
  return useContext(AuthContext);
};

const SYSTEM_USER_OVERRIDE_KEY = "roboto_override_system_user_view";

export const useSystemUser = () => {
  const { currentUser } = useAuth();
  const [isOverrideEnabled, setIsOverrideEnabled] = useState(
    () => localStorage.getItem(SYSTEM_USER_OVERRIDE_KEY) === "true",
  );

  const isActualSystemUser = currentUser?.is_system_user ?? false;

  // Listen for storage changes from other components
  useEffect(() => {
    const handleStorageChange = () => {
      setIsOverrideEnabled(
        localStorage.getItem(SYSTEM_USER_OVERRIDE_KEY) === "true",
      );
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const toggleSystemUserOverride = useCallback(() => {
    if (!isActualSystemUser) {
      return;
    }

    const newOverrideState = !isOverrideEnabled;
    if (newOverrideState) {
      localStorage.setItem(SYSTEM_USER_OVERRIDE_KEY, "true");
    } else {
      localStorage.removeItem(SYSTEM_USER_OVERRIDE_KEY);
    }

    setIsOverrideEnabled(newOverrideState);
    // Keep the storage event for other tabs/windows
    window.dispatchEvent(new Event("storage"));
  }, [isActualSystemUser, isOverrideEnabled]);

  return {
    isSystemUser: isActualSystemUser && !isOverrideEnabled,
    isActualSystemUser,
    isOverrideEnabled,
    toggleSystemUserOverride,
  };
};

/**
 * True when the current user is an admin or owner of the currently-selected
 * org. Reads `myOrgRoles`, which carries the caller's role in each of their
 * orgs, and selects the entry for the active org; returns `false` while roles
 * are still loading (`myOrgRoles === null`). This is purely the org-role check
 * — it does NOT treat Roboto system users as admins. Use {@link useIsAdminUser}
 * when staff should also qualify.
 */
export const useIsOrgAdmin = (): boolean => {
  const { currentOrganization, myOrgRoles } = useAuth();
  const myRoles = myOrgRoles?.find(
    (entry) => entry.org.org_id === currentOrganization?.org_id,
  )?.roles;
  return myRoles?.some((role) => role === "admin" || role === "owner") ?? false;
};

/**
 * True when the current user should see org-admin UI: an admin/owner of the
 * active org (see {@link useIsOrgAdmin}), or a Roboto system user (staff get
 * admin-level UI everywhere, mirroring UsersTable's `ownerOrAdminOrSystemUser`).
 * This is the predicate behind the admin-only Metric Definitions tab (and is the
 * right hook for any other org-admin UI gating). Respects the system-user view
 * override.
 */
export const useIsAdminUser = (): boolean => {
  const isOrgAdmin = useIsOrgAdmin();
  const { isSystemUser } = useSystemUser();
  return isOrgAdmin || isSystemUser;
};

export const useCurrentOrg = (): OrgRecord | null => {
  const auth = useAuth();
  return auth.currentOrganization;
};

export const useCurrentOrgId = (
  // This is optional so components which use this hook can fire an optional prop into this without doing any
  // further validation, and verify that it'll resolve to something meaningful.
  overrideOrgId?: string,
): string => {
  const auth = useAuth();

  if (overrideOrgId) {
    return overrideOrgId;
  }

  if (auth.currentOrganization?.org_id) {
    return auth.currentOrganization.org_id;
  }

  throw new Error(
    "Could not resolve an org_id from useAuth organization or from overrideOrgId",
  );
};
